import React from 'react'
import { format } from 'date-fns'
import {
  Icon,
  Popup
} from 'semantic-ui-react'
// Local
import {
  FORMAT,
  LOCK,
  REVIEW
} from '../../../constant'
import { isExpired } from '../../common/dateTimeUtils'
import { coderQueryExpireHours } from '../../common/currentUserUtils'
import { User } from '../../authenticated/types'

type Lock = {
  createdByUsername: string
  createdById: string
  creationDate: string
  statusCode: string
}

type Review = {
  reviewCode: string
  queryStatusCode?: string
}

type Encounter = {
  coderQueryOutstandingDate?: string
  review: Review
  lock?: Lock
}

type CoderEncounterStatusIconProps = {
  currentUser: User
  encounter: Encounter
}

/**
 * Provides an icon indicating the encounter status, relevant to Coder users
 * @param currentUser Current user
 * @param encounter Encounter record
 */
function CoderEncounterStatusIcon(props: CoderEncounterStatusIconProps): JSX.Element {
  const {
    currentUser,
    encounter
  } = props

  const { review } = encounter
  const { lock } = encounter

  const lockMessage = (lockData: Lock) => {
    const lockOwner = lockData.createdByUsername
    const date = format(
      new Date(lockData.creationDate),
      FORMAT.DATE_TIME
    )
    return `Locked for ${lockOwner}, ${date}`
  }

  const lockColor = (
    lockData: Lock,
    user: User
  ): 'green' | 'black' => lockData.createdById === user.userId
    ? 'green'
    : 'black'

  const hasQueryOutstanding = (enc: Encounter): boolean => !!enc.coderQueryOutstandingDate

  const hasQueryExpired = (
    enc: Encounter,
    user: User
  ): boolean => {
    if (enc.coderQueryOutstandingDate) {
      const expireHours = coderQueryExpireHours(user)
      return isExpired(
        enc.coderQueryOutstandingDate,
        expireHours
      )
    }
    return false
  }

  const isConditionQueryOutstanding = hasQueryOutstanding(encounter)
  const isConditionQueryExpired = hasQueryExpired(
    encounter,
    currentUser
  )
  const expireHours = coderQueryExpireHours(currentUser)

  return (
    <span>
      { lock && lock.statusCode === LOCK.STATUS.ACTIVE && (
        <Popup
          mouseEnterDelay={ 500 }
          content={ lockMessage(lock) }
          trigger={
            <Icon
              name="lock"
              color={ lockColor(
                lock,
                currentUser
              ) }
            />
          }
        />
      ) }
      { review && review.reviewCode === REVIEW.CODE.COMPLETED && (
        <Popup
          content="Complete"
          mouseEnterDelay={ 1000 }
          trigger={ <Icon name="checkmark" color="green" /> }
        />
      ) }
      { review && review.reviewCode === REVIEW.CODE.READY_FOR_CODER_QA && (
        <Popup
          content="Ready for Coder QA"
          mouseEnterDelay={ 1000 }
          trigger={ <Icon name="question circle" color="yellow" /> }
        />
      ) }
      { (isConditionQueryOutstanding && !isConditionQueryExpired) && (
        <Popup
          content="Outstanding Query"
          mouseEnterDelay={ 1000 }
          trigger={ <Icon name="comment alternate outline" /> }
        />
      ) }
      { isConditionQueryExpired && (
        <Popup
          content={ `Outstanding Query Greater Than ${expireHours} Hours` }
          mouseEnterDelay={ 1000 }
          trigger={ <Icon name="comment alternate outline" color="red" /> }
        />
      ) }
      { encounter.review.queryStatusCode === REVIEW.QUERY_STATUS.REVIEW_QUERY_OUTSTANDING && (
        <Popup
          content="Coder Review Requested"
          mouseEnterDelay={ 1000 }
          trigger={ <Icon name="hourglass half" color="red" /> }
        />
      ) }
    </span>
  )
}

export default CoderEncounterStatusIcon
